<template>
  <v-row no-gutters>
    <v-col cols="12" sm="12">
      <v-toolbar color="white" height="56px" flat tile>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              color="primaryDark"
              small
              :disabled="!isDirty"
              @click="onUpdateDefaultOrderConfiguration()"
            >
              <v-icon size="18" class="mr-1">mdi-floppy</v-icon>
              {{ $t("admin.update") }}
            </a-btn>
          </template>
          <span>{{ $t("admin.update") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              class="ml-2"
              color="primaryDark"
              small
              @click="onCancel()"
              :disabled="!isDirty"
            >
              <v-icon size="18" class="mr-1">mdi-close-thick</v-icon>
              {{ $t("btnCancel") }}
            </a-btn>
          </template>
          <span>{{ $t("btnCancel") }}</span>
        </v-tooltip>
      </v-toolbar>
    </v-col>
    <v-col cols="12" sm="12">
      <a-data-table
        :headers="headers"
        :items="sortData"
        item-key="value"
        :no-data-text="$t('noData')"
        :no-results-text="$t('emptySearchRecords')"
        :disable-sort="true"
        :hide-default-footer="true"
      >
        <template v-slot:item.title="{ item }">
          {{ $t(`documents.${item.title}`) }}
        </template>
        <template v-slot:item.sortType="{ item, index }">
          <v-select
            :items="sortTypesList"
            dense
            v-model="item.sortType"
            hide-details=""
          ></v-select>
        </template>
        <template v-slot:item.sortOnColumn="{ item, index }">
          <v-select
            :items="item.sortColumns"
            dense
            v-model="item.sortOnColumn"
            hide-details=""
          >
            <template v-slot:selection="data">
              {{ $t(`admin.${data.item.text}`) }}
            </template>
            <template v-slot:item="data">
              {{ $t(`admin.${data.item.text}`) }}
            </template>
          </v-select>
        </template>
      </a-data-table>
    </v-col>
    <message-sheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></message-sheet>
  </v-row>
</template>

<script>
const defaultSortOrder = [
  {
    id: "a8f7d078-5bf1-4365-9d53-35070229bc64",
    title: "documentByFolder",
    sortOnColumn: "datetime",
    sortColumns: [
      {
        text: "lblDocument",
        value: "name",
      },
      {
        text: "lblUploadTime",
        value: "datetime",
      },
    ],
    sortType: "desc",
  },
  {
    id: "5fae1073-91c2-4bf6-881b-352f5aa76afa",
    title: "documentByAgenda",
    sortOnColumn: "datetime",
    sortColumns: [
      {
        text: "lblDocument",
        value: "fileName",
      },
      {
        text: "lblMeetingTitle",
        value: "meetingName",
      },
      {
        text: "lblTopic",
        value: "topicText",
      },
      {
        text: "lblUploadTime",
        value: "datetime",
      },
    ],
    sortType: "desc",
  },
  {
    id: "2d0190b7-01b6-4cd5-a05d-a8fb5166a9e4",
    title: "documentByMeeting",
    sortOnColumn: "datetime",
    sortColumns: [
      {
        text: "lblDocument",
        value: "fileName",
      },
      {
        text: "lblBelongTo",
        value: "topicText",
      },
      {
        text: "lblUploadTime",
        value: "datetime",
      },
    ],
    sortType: "desc",
  },
];

import { cloneDeep } from "lodash";
import { mapActions, mapGetters } from "vuex";
import { createUUID } from "/src/utils/util";
import MessageSheet from "../MessageSheet.vue";

export default {
  components: { MessageSheet },
  name: "DefaultSortOrder",
  props: {
    setting: {
      type: Object,
      required: true,
    },
    isChangePrevent: {
      type: Boolean,
      default: false,
    },
    isOpenPrevent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tableKey: 0,
      sortData: [],
      originalSortData: null,
      displayMessage: false,
      messageType: "",
      message: "",
    };
  },
  mounted() {
    const tenantConfig = this.tenantSettingConfig(this.setting.configname);
    if (tenantConfig && tenantConfig.id) {
      this.sortData = cloneDeep(tenantConfig[this.setting.configname]);
    } else {
      this.sortData = cloneDeep(defaultSortOrder);
    }
    this.originalSortData = cloneDeep(this.sortData);
  },
  computed: {
    ...mapGetters({
      tenantSettingConfig: "tenant/tenantSettingConfig",
      tenantSettingTypes: "tenant/tenantSettingTypes",
    }),
    tenantId() {
      return localStorage.getItem("tenantId");
    },
    headers() {
      let { columns } = this.setting;
      return columns.map((obj) => {
        return {
          ...obj,
          text: this.$t(`admin.${obj.text}`),
          class: "header header-text",
          width: "33.33%",
        };
      });
    },
    sortTypesList() {
      return [
        { text: this.$t("admin.lblAscending"), value: "asc" },
        { text: this.$t("admin.lblDescending"), value: "desc" },
      ];
    },
    isDirty() {
      return (
        JSON.stringify(this.originalSortData) !== JSON.stringify(this.sortData)
      );
    },
  },
  watch: {
    isDirty(value) {
      this.$emit("update:isChangePrevent", value);
    },
  },
  methods: {
    ...mapActions({
      updateTenantConfig: "tenant/updateTenantConfig",
      createTenantConfig: "tenant/createTenantConfig",
    }),
    getColumnLabel,
    getSortLabel,
    onCancel,
    onUpdateDefaultOrderConfiguration,
    saveOnNavigation,
    showMessageSheet,
  },
};

async function saveOnNavigation() {
  await this.onUpdateDefaultOrderConfiguration();
  if (this.isOpenPrevent) this.$emit("discard");
}

async function onUpdateDefaultOrderConfiguration() {
  try {
    const tenantConfig = this.tenantSettingConfig(this.setting.configname);
    const setting = {
      id: tenantConfig?.id || createUUID(),
      tenantid: tenantConfig?.tenantid || this.tenantId,
      configname: this.setting.configname,
      [this.setting.configname]: this.sortData,
    };

    await (tenantConfig?.id
      ? this.updateTenantConfig(setting)
      : this.createTenantConfig(setting));

    this.$eventHub.$emit("onMessageDisplay", {
      message: this.$t("admin.updateTenantConfigMsg"),
      messageType: "success",
    });
    this.originalSortData = cloneDeep(this.sortData);
    this.$emit("update:isOpenPrevent", false);
    this.$emit("update:isChangePrevent", false);
    this.$emit("switchConfig", true);
  } catch (error) {
    console.error("Update configuration failed:", error);
  }
}

function showMessageSheet(message, messageType) {
  this.displayMessage = true;
  this.message = message;
  this.messageType = messageType;
}

function onCancel() {
  this.sortData = cloneDeep(this.originalSortData);
  this.tableKey++;
}

function getColumnLabel(data) {
  const column = data.sortColumns.find((c) => c.value === data.sortOnColumn);
  return column.text;
}

function getSortLabel(data) {
  const column = this.sortTypesList.find((c) => c.value === data.sortType);
  return column.text;
}

</script>

<style>
</style>